import React, { useState, useMemo, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { Formik, Form } from "formik"
import MultiStepFlowLayout from "./MultiStepFlowLayout"
import MultiStepFlowHeader from "./MultiStepFlowHeader"
import MultiStepFlowFooter from "./MultiStepFlowFooter"
import MultiStepFlowStep from "./MultiStepFlowStep"
import Button from "../Button"
import { cn } from "../../lib/utils"
import { COPYRIGHT_TEXT, LOGO } from "../../constants/textConstants"
import { useNavigate } from "react-router-dom"

/**
 * MultiStepFlowContainer
 *
 * Main orchestrator component for multi-step flows. Manages state, navigation,
 * and form submission across multiple steps.
 *
 * @param {Object} props
 * @param {Array} props.steps - Array of step configuration objects
 * @param {Object} props.initialValues - Initial form values
 * @param {Function} props.onComplete - Callback when flow is completed
 * @param {Function} props.onStepChange - Optional callback when step changes
 * @param {string|React.ReactNode} props.logo - Logo for header
 * @param {boolean} props.showBackButton - Show back button in header
 * @param {boolean} props.showProgress - Show progress indicator
 * @param {string} props.copyrightText - Copyright text for footer
 * @param {number} props.copyrightYear - Copyright year
 * @param {React.ReactNode} props.footerContent - Additional footer content
 * @param {boolean} props.enableBranching - Enable multi-branch flow logic
 * @param {string} props.completeButtonText - Text for complete button
 * @param {string} props.nextButtonText - Text for next button
 * @param {string} props.containerClassName - Additional CSS class for container
 * @param {string} props.variant - Variant of the multi-step flow
 * @param {string} props.fieldWidth - Default width class for all fields (e.g., "w-64 mx-auto")
 * @param {string} props.fieldContainerClassName - Default container class for all fields (e.g., "flex flex-col items-center")
 * @param {Function} props.onBack - Optional custom back handler. If returns true, default back behavior is skipped
 * @param {boolean} props.alwaysShowBackButton - If true, shows back button even on first step
 * @param {React.ReactNode} props.customContentAfterButton - Custom content to render after the next/complete button
 * @param {Object} props.buttonProps - Props to apply to both next and complete buttons (width, bgColor, rounded, padding, etc.)
 * @param {Object} props.nextButtonProps - Props to apply specifically to the next button (overrides buttonProps)
 * @param {Object} props.completeButtonProps - Props to apply specifically to the complete button (overrides buttonProps)
 * @param {number} props.initialStepIndex - Starting step index (for multi-route flows)
 * @param {number} props.totalStepsOverride - Override total steps count for progress calculation (for multi-route flows)
 * @param {number} props.stepOffset - Offset to add to current step when calculating progress (for multi-route flows)
 * @param {string|Array|Object} props.errors - Error message(s) to display. Can be a string, array of strings, or array of objects with {title, description}
 * @param {number} props.stepProgressModifier - Additional modifier to apply to the progress bar (e.g., to show progress one step ahead)
 * @param {boolean} props.hideCompleteButton - If true, hides the complete button and only shows the next button
 *
 */
const MultiStepFlowContainer = ({
  steps,
  initialValues = {},
  onComplete,
  onStepChange,
  logo = LOGO,
  showBackButton = true,
  showProgress = true,
  copyrightText = COPYRIGHT_TEXT,
  copyrightYear,
  footerContent,
  enableBranching = false,
  completeButtonText = "Complete",
  nextButtonText = "Next",
  containerClassName,
  variant = "default",
  fieldWidth,
  fieldContainerClassName,
  onBack: customOnBack,
  alwaysShowBackButton = false,
  customContentAfterButton,
  buttonProps = {},
  nextButtonProps = {},
  completeButtonProps = {},
  initialStepIndex = 0,
  totalStepsOverride,
  stepOffset = 0,
  errors,
  stepProgressModifier = 0,
  hideCompleteButton = false,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex)
  const [formValues, setFormValues] = useState(initialValues)
  const [editableStates, setEditableStates] = useState({})
  const [isSubmittingForm, setIsSubmittingForm] = useState(false)
  const isHandlingSubmitRef = useRef(false)
  const navigate = useNavigate()

  // Reset form values when the initialValues prop changes
  useEffect(() => {
    setFormValues(initialValues)
  }, [initialValues])

  // Calculate active steps based on current form values (for branching)
  const activeSteps = useMemo(() => {
    if (!enableBranching) {
      return steps
    }

    return steps.filter(step => {
      if (!step.shouldShow) return true
      return step.shouldShow(formValues)
    })
  }, [steps, formValues, enableBranching])

  const currentStep = activeSteps[currentStepIndex]
  const isLastStep = currentStepIndex === activeSteps.length - 1

  // Get validation schema for current step (supports both 'validation' and 'schema' properties for backwards compatibility)
  const validationSchema = currentStep.validation || currentStep.schema || null

  const handleNext = async values => {
    if (isHandlingSubmitRef.current) return
    isHandlingSubmitRef.current = true

    try {
      const updatedValues = { ...formValues, ...values }
      setFormValues(updatedValues)

      if (isLastStep) {
        setIsSubmittingForm(true)
        try {
          await onComplete(updatedValues)
        } finally {
          setIsSubmittingForm(false)
        }
      } else {
        const nextStepIndex = currentStepIndex + 1
        const nextStep = activeSteps[nextStepIndex]

        if (nextStep.getInitialValues) {
          const dynamicDefaults = nextStep.getInitialValues(updatedValues)
          setFormValues(prev => ({ ...prev, ...dynamicDefaults }))
        }

        setCurrentStepIndex(nextStepIndex)
        if (onStepChange) {
          onStepChange(nextStepIndex, nextStep)
        }
      }
    } finally {
      isHandlingSubmitRef.current = false
    }
  }

  const handleBack = () => {
    // If custom back handler is provided and it returns true, skip default behavior
    if (customOnBack && customOnBack(currentStepIndex)) {
      return
    }

    if (currentStepIndex > 0) {
      // Move to last step
      setCurrentStepIndex(prev => prev - 1)
      if (onStepChange) {
        onStepChange(currentStepIndex - 1, activeSteps[currentStepIndex - 1])
      }
    }

    if (currentStepIndex === 0) {
      // If on the first step, navigate back to the previous page
      navigate(-1)
    }
  }

  const variantClassName = variant === "form" ? "bg-white p-8 rounded-3xl shadow-lg min-h-fit" : ""

  // Merge button props in order: variant defaults → buttonProps → specific props
  const getButtonProps = () => {
    const defaultProps = {
      bgColor: variant === "form"
        ? "bg-brand hover:bg-brand/90"
        : "bg-brandSecondary hover:bg-brandSecondary/90",
      rounded: variant === "form" ? "" : "rounded-full",
      width: variant === "form" ? "w-full" : "w-fit",
      padding: "px-6 py-4",
      textTransform: "normal-case",
    }

    const specificProps = isLastStep ? completeButtonProps : nextButtonProps

    return {
      ...defaultProps,
      ...buttonProps,
      ...specificProps,
    }
  }

  return (
    <Formik
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={handleNext}
      enableReinitialize
      validateOnChange={true}
      validateOnBlur={false}
    >
      {({ values, isSubmitting, isValid, setTouched, validateForm }) => {
        // Reset touched state when step changes
        useEffect(() => {
          setTouched({})
        }, [currentStepIndex, setTouched])

        // Custom submit handler that marks all error fields as touched
        const handleSubmitWithTouched = async (e) => {
          e.preventDefault()
          const errors = await validateForm()

          if (Object.keys(errors).length > 0) {
            // Mark all fields with errors as touched
            const touchedFields = Object.keys(errors).reduce((acc, field) => {
              acc[field] = true
              return acc
            }, {})
            setTouched(touchedFields)
          } else {
            // If no errors, proceed with actual submit (await to ensure isSubmitting is tracked)
            await handleNext(values)
          }
        }

        return (
          <Form onSubmit={handleSubmitWithTouched}>
            <MultiStepFlowLayout
            header={
              variant === "form" ? null : (
                <MultiStepFlowHeader
                  onBack={handleBack}
                  showBackButton={showBackButton && (currentStepIndex > 0 || alwaysShowBackButton)}
                  alwaysShowBackButton={alwaysShowBackButton}
                  logo={logo}
                  currentStep={currentStepIndex + stepOffset}
                  totalSteps={totalStepsOverride || activeSteps.length}
                  showProgress={showProgress}
                  stepProgressModifier={stepProgressModifier}
                />
              )
            }
            footer={
              variant === "form" ? null : (
                <MultiStepFlowFooter copyrightText={copyrightText} copyrightYear={copyrightYear}>
                  {footerContent}
                </MultiStepFlowFooter>
              )
            }
            errors={errors}
          >
            {/* Render current step */}
            <div
              className={cn(
                "mx-auto min-h-[60vh] max-w-screen-md",
                variantClassName,
                containerClassName,
              )}
            >
              <MultiStepFlowStep
                step={currentStep}
                values={values}
                variant={variant}
                defaultFieldWidth={fieldWidth}
                defaultFieldContainerClassName={fieldContainerClassName}
                editableStates={editableStates}
                setEditableStates={setEditableStates}
              />

              {/* Next/Submit Button */}
              {!hideCompleteButton && (
                <div className="mt-8 flex justify-center">
                  <Button
                    type="submit"
                    {...getButtonProps()}
                    disabled={isSubmitting || isSubmittingForm}
                  >
                    {isSubmitting || isSubmittingForm
                      ? "Please wait..."
                      : currentStep.nextButtonText ||
                        (isLastStep ? completeButtonText : nextButtonText)}
                  </Button>
                </div>
              )}

              {/* Custom content after button */}
              {customContentAfterButton && (
                <div className="mt-4">
                  {customContentAfterButton}
                </div>
              )}
            </div>
          </MultiStepFlowLayout>
          </Form>
        )
      }}
    </Formik>
  )
}

MultiStepFlowContainer.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      encouragement: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      encouragementClassName: PropTypes.string,
      titleClassName: PropTypes.string,
      subtitleClassName: PropTypes.string,
      fieldType: PropTypes.string,
      fieldName: PropTypes.string,
      label: PropTypes.string,
      instructions: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
      fieldProps: PropTypes.object,
      fields: PropTypes.arrayOf(
        PropTypes.shape({
          fieldType: PropTypes.string.isRequired,
          fieldName: PropTypes.string,
          label: PropTypes.string,
          fieldProps: PropTypes.object,
          content: PropTypes.node,
          showWhen: PropTypes.func,
          instructions: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
        })
      ),
    content: PropTypes.node,
    renderCustomContent: PropTypes.func,
    contentAfterField: PropTypes.bool,
    shouldShow: PropTypes.func,
    }),
  ).isRequired,
  initialValues: PropTypes.object,
  onComplete: PropTypes.func.isRequired,
  onStepChange: PropTypes.func,
  logo: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  showBackButton: PropTypes.bool,
  showProgress: PropTypes.bool,
  copyrightText: PropTypes.string,
  copyrightYear: PropTypes.number,
  footerContent: PropTypes.node,
  enableBranching: PropTypes.bool,
  completeButtonText: PropTypes.string,
  nextButtonText: PropTypes.string,
  containerClassName: PropTypes.string,
  variant: PropTypes.oneOf(["default", "form"]),
  fieldWidth: PropTypes.string,
  fieldContainerClassName: PropTypes.string,
  onBack: PropTypes.func,
  alwaysShowBackButton: PropTypes.bool,
  customContentAfterButton: PropTypes.node,
  buttonProps: PropTypes.object,
  nextButtonProps: PropTypes.object,
  completeButtonProps: PropTypes.object,
  initialStepIndex: PropTypes.number,
  totalStepsOverride: PropTypes.number,
  stepOffset: PropTypes.number,
  errors: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
      })
    ])),
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    })
  ]),
  stepProgressModifier: PropTypes.number,
  hideCompleteButton: PropTypes.bool,
}

export default MultiStepFlowContainer
